export const collapsible = { 
  selector: '.v-component--collapsible', 
  reactive: true,

  run(el, data, root) {
    const header = el.querySelector('[data-collapsible-header]');
    const content = el.querySelector('[data-collapsible-content]');
    const closeButton = el.querySelector('[data-close-collapsible]');
    const autoCloseDelay = parseInt(el.getAttribute('data-auto-close'), 10);
    const closeOnBlur = el.hasAttribute('data-close-on-blur');

    if (!header || !content) return;

    const headerId = header.id || 'collapsible-header';
    const contentId = content.id || 'collapsible-content';
    header.id = headerId;
    content.id = contentId;

    // Live region for ARIA announcements
    let liveRegion = el.querySelector('[aria-live]');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('class', 'sr-only');
      el.appendChild(liveRegion);
    }

    // Add screen reader instructions
    const instructions = document.createElement('span');
    instructions.className = 'sr-only';
    instructions.textContent = 'Press Enter or Space to toggle this section.';
    header.appendChild(instructions);

    // Set ARIA
    header.setAttribute('role', 'button');
    header.setAttribute('aria-controls', contentId);
    header.setAttribute('tabindex', '0');
    content.setAttribute('role', 'region');
    content.setAttribute('aria-labelledby', headerId);

    // Persistent state key
    const storageKey = `collapsible-${contentId}`;
    const storedState = localStorage.getItem(storageKey);
    const attributeState = header.getAttribute('aria-expanded') === 'true' || el.hasAttribute('data-open');
    const isInitiallyOpen = storedState === 'open' || (storedState === null && attributeState);

    let autoCloseTimeout = null;

    function updateState(isOpen) {
      header.setAttribute('aria-expanded', String(isOpen));
      el.setAttribute('data-state', isOpen ? 'open' : 'closed');
      localStorage.setItem(storageKey, isOpen ? 'open' : 'closed');

      if (isOpen) {
        content.removeAttribute('hidden');
        liveRegion.textContent = 'Section expanded';
      } else {
        content.setAttribute('hidden', 'true');
        liveRegion.textContent = 'Section collapsed';
      }
    }

    function open() {
      updateState(true);
      if (autoCloseDelay) {
        clearTimeout(autoCloseTimeout);
        autoCloseTimeout = setTimeout(close, autoCloseDelay);
      }
    }

    function close() {
      updateState(false);
    }

    function toggle() {
      const isOpen = header.getAttribute('aria-expanded') === 'true';
      isOpen ? close() : open();
    }

    // Set initial state
    updateState(isInitiallyOpen);

    // Click & key listeners
    header.addEventListener('click', toggle);
    header.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      } else if (e.key === 'Escape') {
        close();
      }
    });

    // Optional internal close button
    if (closeButton) {
      closeButton.addEventListener('click', close);
    }

    // Click outside to close
    if (closeOnBlur) {
      document.addEventListener('click', e => {
        if (!el.contains(e.target)) {
          close();
        }
      });
    }

    // Hash deep link support
    function openByHash(hash) {
      if (!hash) return;
      if (content.id === hash || header.getAttribute('aria-controls') === hash) {
        open();
        header.focus();
      }
    }

    openByHash(window.location.hash.substring(1));
    window.addEventListener('hashchange', () => {
      openByHash(window.location.hash.substring(1));
    });

    // Expose programmatic API
    el.collapsible = { open, close, toggle };
  }
};
